import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, LoaderCircle, X } from 'lucide-react';

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div><h1>{title}</h1>{description && <p>{description}</p>}</div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Card({ children, title, actions, className = '' }: { children: ReactNode; title?: string; actions?: ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && <div className="card-header"><h2>{title}</h2><div>{actions}</div></div>}
      <div className="card-body">{children}</div>
    </section>
  );
}

export function Button({
  children,
  variant = 'primary',
  busy = false,
  className = '',
  disabled = false,
  type = 'button',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'warning' | 'ghost';
  busy?: boolean;
}) {
  return (
    <button
      {...props}
      type={type}
      className={`button button-${variant} ${className}`.trim()}
      disabled={busy || disabled}
      aria-busy={busy || undefined}
    >
      {busy && <LoaderCircle className="spin" size={16} />}
      {children}
    </button>
  );
}

export function StatusBadge({ value }: { value: string | null | undefined }) {
  const normalized = value || 'SIN_ESTADO';
  const tone = /ERROR|ANULADO|NO_REALIZADO/.test(normalized) ? 'danger' : /CERRADO|ENVIADO|APROBADO|ACTIVO|GENERADO|REALIZADO/.test(normalized) ? 'success' : /PEND|DEVUELTO|INCOMPLETO/.test(normalized) ? 'warning' : 'info';
  return <span className={`badge badge-${tone}`}>{normalized.replaceAll('_', ' ')}</span>;
}

export function Field({ label, error, required, children, hint }: { label: string; error?: string | undefined; required?: boolean | undefined; children: ReactNode; hint?: string | undefined }) {
  return <label className="field"><span>{label}{required && <strong> *</strong>}</span>{children}{hint && <small>{hint}</small>}{error && <em>{error}</em>}</label>;
}

export function EmptyState({ title, description }: { title: string; description: string }) {
  return <div className="empty"><AlertCircle size={32} /><h3>{title}</h3><p>{description}</p></div>;
}

export function Loading({ label = 'Cargando…' }: { label?: string }) {
  return <div className="loading"><LoaderCircle className="spin" /> {label}</div>;
}

export function Notice({ kind = 'success', children, onClose }: { kind?: 'success' | 'error' | 'info'; children: ReactNode; onClose?: () => void }) {
  return <div className={`notice notice-${kind}`}>{kind === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}<span>{children}</span>{onClose && <button type="button" onClick={onClose} aria-label="Cerrar mensaje"><X size={16} /></button>}</div>;
}

export function Tabs({ items, active, onChange }: { items: { key: string; label: string }[]; active: string; onChange: (key: string) => void }) {
  return <div className="tabs">{items.map((item) => <button type="button" key={item.key} className={active === item.key ? 'active' : ''} onClick={() => onChange(item.key)}>{item.label}</button>)}</div>;
}
